import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { appendAiccUserParams } from '../utils/aiccIdentity';
import { 
  ArrowLeft, 
  Maximize2, 
  Minimize2, 
  Clock, 
  Zap,
  CheckCircle,
  AlertCircle,
  Loader2,
  Globe,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
const API = `${API_BASE}/api`;

// Trusted domains for SCORM Cloud / Intuition
const TRUSTED_DOMAINS = [
  'cloud.scorm.com',
  'app.cloud.scorm.com',
  'dispatch.cloud.scorm.com',
  'intuition.com',
  'app.intuition.com',
  'content.intuition.com',
  'learn.intuition.com',
];

export default function ExternalLearningPlayer() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const iframeRef = useRef(null);
  
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(true); // Start in fullscreen mode
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [completionProcessing, setCompletionProcessing] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [canManuallyComplete, setCanManuallyComplete] = useState(false);

  // Fetch module and progress on mount
  useEffect(() => {
    fetchModuleData();
  }, [moduleId]);

  // Enable manual completion after 30 seconds of engagement
  useEffect(() => {
    setCanManuallyComplete(false);
    const timer = setTimeout(() => {
      setCanManuallyComplete(true);
    }, 30000); // 30 seconds minimum engagement
    return () => clearTimeout(timer);
  }, [moduleId]);

  // Listen for SCORM/AICC postMessage events
  useEffect(() => {
    const handleMessage = async (event) => {
      // Validate origin
      const origin = new URL(event.origin);
      const isTrusted = TRUSTED_DOMAINS.some(domain => 
        origin.hostname === domain || origin.hostname.endsWith(`.${domain}`)
      );
      
      if (!isTrusted) {
        console.warn('Received postMessage from untrusted origin:', event.origin);
        return;
      }
      
      try {
        // Parse message data
        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            // May be a comma-separated SCORM command
            const parts = data.split(',');
            if (parts.length >= 2) {
              data = { command: parts[0], element: parts[1], value: parts[2] };
            } else {
              return;
            }
          }
        }
        
        console.log('SCORM postMessage received:', data);
        
        // Handle completion signals
        if (data.type === 'completion' || 
            data.command === 'Terminate' || 
            data.element === 'cmi.core.lesson_status' ||
            data.element === 'cmi.completion_status') {
          
          const status = data.value || data.status || 'completed';
          
          if (['completed', 'passed', 'complete'].includes(status.toLowerCase())) {
            // Mark as coming from postMessage and include the origin for callback
            await handleModuleCompletion({
              ...data,
              fromPostMessage: true,
              origin: event.origin
            });
          }
        }
        
        // Handle SetValue for score
        if (data.command === 'SetValue' && 
            (data.element === 'cmi.core.score.raw' || data.element === 'cmi.score.raw')) {
          console.log('Score received:', data.value);
        }
        
      } catch (error) {
        console.error('Error processing SCORM postMessage:', error);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [module, user, token]);

  // Handle fullscreen toggle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const fetchModuleData = async () => {
    setLoading(true);
    try {
      // Fetch module details
      const moduleRes = await axios.get(`${API}/external-learning/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModule(moduleRes.data);
      
      // Fetch user progress
      const progressRes = await axios.get(`${API}/external-learning/progress/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(progressRes.data);
      
      // Mark as started if not already
      if (progressRes.data.status === 'not_started') {
        await axios.post(`${API}/external-learning/progress/${moduleId}/start`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
    } catch (error) {
      console.error('Error fetching module:', error);
      toast.error('Failed to load external learning module');
      navigate('/external-learning');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleCompletion = async (scormData = {}) => {
    if (completionProcessing) return;
    
    setCompletionProcessing(true);
    
    // First, send completion to the main progress endpoint
    try {
      const response = await axios.post(`${API}/external-learning/progress/${moduleId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show success toast immediately after main completion succeeds
      toast.success(
        <div className="flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-400" />
          <div>
            <p className="font-medium">Module Completed!</p>
            <p className="text-sm text-zinc-400">+{response.data.xp_awarded || module?.xp_reward || 100} XP earned</p>
          </div>
        </div>
      );
      
      // Update local progress state immediately
      setProgress(prev => ({
        ...prev,
        status: 'completed',
        xp_awarded: response.data.xp_awarded || module?.xp_reward || 100
      }));
      
      // Only send completion callback if we have SCORM data from a postMessage (not manual completion)
      // The completion-callback endpoint expects the origin to be a trusted SCORM provider, not our own domain
      if (scormData && scormData.fromPostMessage) {
        try {
          await axios.post(`${API}/external-learning/completion-callback`, {
            module_id: moduleId,
            user_id: user?.user_id,
            tenant_id: user?.organization_id || user?.org_id || 'default',
            completion_status: 'completed',
            score: scormData.score || null,
            time_spent_seconds: scormData.timeSpent || null,
            lesson_status: scormData.value || 'completed',
            metadata: scormData
          }, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'X-External-Origin': scormData.origin || ''
            }
          });
        } catch (callbackError) {
          // Callback is best-effort for SCORM data sync - don't fail the completion
          console.log('SCORM callback relay skipped or failed:', callbackError.message);
        }
      }
      
    } catch (error) {
      console.error('Error recording completion:', error);
      toast.error('Failed to record completion. Please try again.');
    } finally {
      setCompletionProcessing(false);
    }
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } catch (error) {
        console.error('Error entering fullscreen:', error);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
      }
    }
  };

  const handleExit = () => {
    if (progress?.status === 'in_progress') {
      setShowExitConfirm(true);
    } else {
      navigate('/external-learning');
    }
  };

  const confirmExit = () => {
    // Exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    navigate('/external-learning');
  };

  // Manual completion button handler
  const handleManualComplete = async () => {
    await handleModuleCompletion({ manual: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-navigate-gold animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading external content...</p>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Module Not Found</h2>
          <p className="text-zinc-400 mb-4">The requested external learning module could not be found.</p>
          <Button onClick={() => navigate('/external-learning')} variant="outline">
            Back to External Learning
          </Button>
        </div>
      </div>
    );
  }

  const isCompleted = progress?.status === 'completed' || progress?.status === 'passed';

  return (
    <div className="h-screen bg-navigate-dark flex flex-col overflow-hidden" data-testid="external-learning-player">
      {/* Header Bar - Fixed at top */}
      <header className="flex-shrink-0 bg-navigate-surface border-b border-zinc-800 px-4 py-3 flex items-center justify-between z-50">
        <div className="flex items-center gap-4">
          <button
            onClick={handleExit}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="exit-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="hidden sm:inline">Exit</span>
          </button>
          
          <div className="h-6 w-px bg-zinc-700" />
          
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Globe className="h-4 w-4 text-blue-400" />
            </div>
            <div>
              <h1 className="text-white font-medium text-sm sm:text-base line-clamp-1">{module.title}</h1>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span className="capitalize">{module.provider?.replace('_', ' ') || 'SCORM Cloud'}</span>
                <span>•</span>
                <Clock className="h-3 w-3" />
                <span>{module.estimated_duration_minutes || 30} mins</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Completion Status */}
          {isCompleted ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span className="text-green-300 text-sm font-medium">Completed</span>
              <span className="text-green-400 text-xs">+{progress?.xp_awarded || module.xp_reward} XP</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-navigate-gold/20 border border-navigate-gold/30 rounded-lg">
              <Zap className="h-4 w-4 text-navigate-gold" />
              <span className="text-navigate-gold text-sm font-medium">{module.xp_reward || 100} XP</span>
            </div>
          )}
          
          {/* Manual Complete Button (only if not completed and after engagement period) */}
          {!isCompleted && (
            <Button
              onClick={handleManualComplete}
              disabled={completionProcessing || !canManuallyComplete}
              size="sm"
              className={`text-white ${
                canManuallyComplete 
                  ? 'bg-green-600 hover:bg-green-500' 
                  : 'bg-zinc-600 cursor-not-allowed'
              }`}
              title={canManuallyComplete ? 'Mark this module as complete' : 'Complete the content to unlock (30s minimum)'}
              data-testid="manual-complete-btn"
            >
              {completionProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Mark Complete
                </>
              )}
            </Button>
          )}
          
          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            data-testid="fullscreen-toggle"
          >
            {isFullscreen ? (
              <Minimize2 className="h-5 w-5" />
            ) : (
              <Maximize2 className="h-5 w-5" />
            )}
          </button>
        </div>
      </header>

      {/* Iframe Container - Takes remaining space */}
      <div className="flex-1 relative bg-black min-h-0">
        {!iframeLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-navigate-dark z-10">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading external content...</p>
              <p className="text-xs text-zinc-600 mt-2">This may take a moment</p>
            </div>
          </div>
        )}
        
        <iframe
          ref={iframeRef}
          src={appendAiccUserParams(module.iframe_url, user)}
          title={module.title}
          className="absolute inset-0 w-full h-full border-0"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          onLoad={() => setIframeLoaded(true)}
          data-testid="external-learning-iframe"
        />
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="exit-confirm-modal">
          <div className="bg-navigate-surface border border-zinc-700 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Exit Module?</h3>
              <button 
                onClick={() => setShowExitConfirm(false)}
                className="p-1 text-zinc-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-zinc-400 mb-6">
              You haven&apos;t completed this module yet. Your progress will be saved, 
              but you won&apos;t receive XP until you complete it.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowExitConfirm(false)}
                className="flex-1"
              >
                Continue Learning
              </Button>
              <Button
                onClick={confirmExit}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600"
              >
                Exit Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
